import { z } from "zod";
import { useMutation, useQuery } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { classifyTemplateUseFailure } from "./templateUseFailure";

export const ACTION_PLAN_ESTIMATE_TIMEOUT_MS = 15_000;
export const ACTION_PLAN_CREATION_LOOKUP_TIMEOUT_MS = 15_000;
export const ACTION_PLAN_CREATION_RECONCILIATION_WINDOW_MS = 120_000;
export const ACTION_PLAN_CREATION_RECONCILIATION_INTERVAL_MS = 5_000;
export const ActionPlanInvitationSchema = z.object({ eligible: z.boolean(), templateId: z.string().nullish() });
const TemplatePlanEstimateSchema = z.discriminatedUnion("allActionsDropped", [
    z.object({
        allActionsDropped: z.literal(true),
        values: z.null().optional(),
        templateVersion: z.number().int().positive(),
    }),
    z.object({
        allActionsDropped: z.literal(false),
        values: z.record(z.string(), z.number().finite().nullable()),
        templateVersion: z.number().int().positive(),
    }),
]);
export type ActionPlanInvitation = z.infer<typeof ActionPlanInvitationSchema>;
export type TemplatePlanInput = { templateId: string; endCampaignId: number };
export type TemplateAdoptionInput = TemplatePlanInput & { templateVersion: number; name: string };

export class ActionPlanAlreadyExistsError extends Error {
    constructor(
        message: string,
        readonly existingPlanId?: string,
    ) {
        super(message);
    }
}

export const actionPlanInvitationQueryKey = "actionPlanInvitation" as const;
export const actionPlanEstimateQueryKey = "actionPlanEstimate" as const;

export const getActionPlanInvitation = async (postId: number) => {
    const response = await loggedInApiCall.get(`/api/ssq-posts/${postId}/action-plan-invitation`, {
        headers: { Accept: "application/json" },
    });
    return ActionPlanInvitationSchema.parse(response.data);
};

export const estimateTemplatePlan = async (postId: number, input: TemplatePlanInput, signal?: AbortSignal) => {
    const response = await loggedInApiCall.post(`/api/ssq-posts/${postId}/action-plan-estimate`, input, {
        signal,
        timeout: ACTION_PLAN_ESTIMATE_TIMEOUT_MS,
        headers: { "Content-Type": "application/json", Accept: "application/json" },
    });
    return TemplatePlanEstimateSchema.parse(response.data);
};

export const adoptTemplatePlan = async (postId: number, input: TemplateAdoptionInput) => {
    const response = await loggedInApiCall.post(`/api/ssq-posts/${postId}/action-plan-adoption`, input, {
        headers: { "Content-Type": "application/json", Accept: "application/json" },
    });
    return z.object({ id: z.string() }).parse(response.data);
};

export const findActionPlanForPost = async (postId: number, signal?: AbortSignal) => {
    const response = await loggedInApiCall.get("/api/action_plans", {
        params: { "startSsqPost.id": postId, itemsPerPage: 1 },
        signal,
        timeout: ACTION_PLAN_CREATION_LOOKUP_TIMEOUT_MS,
        headers: { Accept: "application/ld+json" },
    });
    return z.object({ "hydra:member": z.array(z.object({ id: z.string() })) }).parse(response.data)["hydra:member"][0]
        ?.id;
};

export const hasActionPlanForPost = async (postId: number, signal?: AbortSignal) =>
    Boolean(await findActionPlanForPost(postId, signal));

export type ActionPlanCreationReconciliation =
    | { status: "created"; planId: string }
    | { status: "unconfirmed" }
    | { status: "aborted" };

const waitForReconciliationCheck = (delay: number, signal: AbortSignal) =>
    new Promise<boolean>((resolve) => {
        if (signal.aborted) {
            resolve(false);
            return;
        }

        const timer = window.setTimeout(() => {
            signal.removeEventListener("abort", abort);
            resolve(true);
        }, delay);
        const abort = () => {
            window.clearTimeout(timer);
            resolve(false);
        };
        signal.addEventListener("abort", abort, { once: true });
    });

export const reconcileActionPlanCreation = async (
    postId: number,
    startedAt: number,
    signal: AbortSignal,
): Promise<ActionPlanCreationReconciliation> => {
    const deadline = startedAt + ACTION_PLAN_CREATION_RECONCILIATION_WINDOW_MS;

    while (!signal.aborted) {
        try {
            const planId = await findActionPlanForPost(postId, signal);

            if (planId) {
                return { status: "created", planId };
            }
        } catch {
            if (signal.aborted) {
                return { status: "aborted" };
            }
        }

        const remaining = deadline - Date.now();

        if (remaining <= 0) {
            return { status: "unconfirmed" };
        }

        const shouldContinue = await waitForReconciliationCheck(
            Math.min(ACTION_PLAN_CREATION_RECONCILIATION_INTERVAL_MS, remaining),
            signal,
        );

        if (!shouldContinue) {
            return { status: "aborted" };
        }
    }

    return { status: "aborted" };
};

export const useActionPlanInvitation = (
    postId: number,
    enabled: boolean,
    options?: { onSuccess?: (data: ActionPlanInvitation) => void; onError?: (error: unknown) => void },
) =>
    useQuery({
        queryKey: [actionPlanInvitationQueryKey, postId],
        queryFn: () => getActionPlanInvitation(postId),
        enabled,
        cacheTime: 0,
        retry: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        onSuccess: options?.onSuccess,
        onError: options?.onError,
    });

export const useActionPlanEstimate = (
    postId: number,
    templateId: string | null | undefined,
    endCampaignId: number | undefined,
    enabled: boolean,
) =>
    useQuery({
        queryKey: [actionPlanEstimateQueryKey, postId, templateId, endCampaignId],
        queryFn: async ({ signal }) => {
            if (!templateId || endCampaignId === undefined) {
                throw new Error("Missing plan settings");
            }

            try {
                return await estimateTemplatePlan(postId, { templateId, endCampaignId }, signal);
            } catch (error) {
                const failure = classifyTemplateUseFailure(error);

                if (failure.kind === "plan_already_exists") {
                    throw new ActionPlanAlreadyExistsError("An action plan already exists", failure.existingPlanId);
                }

                throw error;
            }
        },
        enabled,
        retry: false,
        cacheTime: 0,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });

export const useAdoptTemplatePlan = () =>
    useMutation(({ postId, input }: { postId: number; input: TemplateAdoptionInput }) =>
        adoptTemplatePlan(postId, input),
    );
